import { useTranslate } from '@repo/shared/hooks'
import { EMAIL_REGEX, isValidMobile } from '@repo/ui/lib'
import { z } from 'zod'

export const useTransferSchema = (balance: number) => {
  const { t } = useTranslate()
  const mobileSchema = z.object({
    mobile: z
      .string({
        message: t('loginPage_validation_mobile_numbersOnly'),
      })
      .superRefine((val, ctx) => {
        if (!val.startsWith('09')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('loginPage_validation_mobile_startWith09'),
          })
        }
        if (val && val.length !== 11) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('loginPage_validation_mobile_length'),
          })
          return
        }
        if (!isValidMobile(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('loginPage_validation_mobile_numbersOnly'),
          })
        }
      }),
  })

  const emailSchema = z.object({
    email: z
      .string({
        message: t('registerPage_validation_email_required'),
      })
      .email({
        message: t('registerPage_validation_email_invalid'),
      })
      .regex(EMAIL_REGEX, {
        message: t('registerPage_validation_email_invalid'),
      }),
  })

  const schema = z.object({
    transferTo: z
      .string({
        message: t('loginPage_validation_mobile_email_required'),
      })
      .superRefine((val, ctx) => {
        const isEmail = /[^0-9۰-۹٠-٩]/.test(val)
        if (isEmail) {
          const result = emailSchema.safeParse({ email: val })
          if (!result.success) {
            result.error.errors.forEach((err) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: err.message,
              })
            })
          }
        } else {
          if (!val.startsWith('09')) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('loginPage_validation_mobile_startWith09'),
            })
          }
          if (!isValidMobile(val)) {
            const result = mobileSchema.safeParse({ mobile: val })
            if (!result.success) {
              result.error.errors.forEach((err) => {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: err.message,
                })
              })
            }
          }
        }
      }),
    selectedAsset: z.object(
      {
        token: z.string({
          message: t('transfer_token_required'),
        }),
      },
      {
        message: t('transfer_token_required'),
      },
    ),
    amount: z
      .number({
        required_error: t('transfer_amount_required'),
      })
      .refine(
        (val) => {
          return +val > 0
        },
        {
          message: t('transfer_amount_min'),
        },
      )
      .refine(
        (val) => {
          return +val <= +balance
        },
        {
          message: t('transfer_amount_max_balance'),
        },
      ),
    range: z.number({
      required_error: t('transfer_amount_required'),
    }),
  })

  return { schema }
}

export type TransferFormValues = z.infer<
  ReturnType<typeof useTransferSchema>['schema']
>
